import React, { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { useEpisodeDetail, useMovieDetail } from '../hooks/useMovie';
import { toTitle } from '../lib/catalogue';
import { CinemaPlayer } from '../components/player/CinemaPlayer';
import { usePageTitle } from '../hooks/usePageTitle';

export default function WatchPage() {
  const { episodeId } = useParams();
  const epId = Number(episodeId);

  // First hop: resolve which movie/series this episode belongs to.
  const { data: epBootstrap, isLoading: epLoading, error: epError } = useEpisodeDetail(epId);
  const movieId = epBootstrap?.MovieId;

  // Second hop: the full title — poster, trailer, and (for a series) every season/episode —
  // feeds the player's episodes drawer and next-episode autoplay.
  const { data: movieData, isLoading: movieLoading, error: movieError } = useMovieDetail(movieId);

  const title = useMemo(() => {
    if (!movieData) return null;
    const base = toTitle(movieData);
    return { ...base, seasons: movieData.Seasons || [], episodes: movieData.Episodes || [] };
  }, [movieData]);

  usePageTitle(title ? `Watching ${title.title} · Kast Player` : 'Kast Player');

  const isLoading = epLoading || (!!movieId && movieLoading);
  const hasError = epError || movieError || (!epLoading && !epBootstrap);

  if (isLoading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black">
        <Icon icon="ph:spinner-gap-bold" className="size-10 animate-spin text-white/60" />
      </div>
    );
  }

  if (hasError || !title) {
    return (
      <div className="fixed inset-0 z-50 flex flex-col items-center justify-center gap-4 bg-black px-6 text-center text-white">
        <Icon icon="ph:film-slate" className="size-16 text-white/30" />
        <h1 className="text-xl font-bold">Episode not available</h1>
        <p className="max-w-sm text-sm text-white/60">
          This episode could not be loaded. It may have been removed or is no longer available.
        </p>
        <Link to="/" className="mt-2 rounded-full border border-white/20 px-5 py-2 text-sm font-medium hover:bg-white/10">
          Back to Kast
        </Link>
      </div>
    );
  }

  return <CinemaPlayer title={title} initialEpisodeId={epId} />;
}
